import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaUser, FaList, FaEdit, FaPlus } from 'react-icons/fa';
import * as clientService from '../../services/client.service';
import PageHeader from '../../components/PageHeader/PageHeader';
import './ClientList.css';

const ClientList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 25,
    last_page: 1,
  });

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch clients on mount and when page/debounced search changes
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);
        const result = await clientService.getClientList(currentPage, 25, debouncedSearch);
        setClients(Array.isArray(result.data) ? result.data : []);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Failed to load clients:', err);
        setError('Failed to load clients');
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    loadClients();
  }, [currentPage, debouncedSearch]);

  const handleViewModify = (client) => {
    navigate(`/dashboard/crm-master/edit-client/${client.id}`);
  };

  return (
    <div className="page-shell">
      <PageHeader
        icon={<FaList size={22} />}
        title="Client List"
        subtitle="View and modify client (buyer ledger) records"
        actions={(
          <Link to="/dashboard/crm-master/add-client" className="btn btn-light">
            <FaPlus className="me-2" /> Add Client
          </Link>
        )}
      />
      <div className="container-lg mt-2">

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        <div className="card border-0 shadow-sm clientlist-card">
          <div className="card-body">
            {/* Search Bar */}
            <div className="mb-3">
              <div className="input-group">
                <span className="input-group-text">🔍</span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by client name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading clients...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FaUser size={48} className="mb-3 icon-dim" />
                <p className="mb-3">{searchTerm ? 'No clients found matching your search.' : 'No clients added yet.'}</p>
                <Link to="/dashboard/crm-master/add-client" className="btn btn-outline-primary">
                  <FaPlus className="me-2" /> Add your first client
                </Link>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover table-bordered align-middle">
                    <thead className="thead-muted">
                      <tr>
                        <th className="fw-600 text-dark-700">Client Code</th>
                        <th className="fw-600 text-dark-700">Client Name</th>
                        <th className="fw-600 text-dark-700">Client Type</th>
                        <th className="fw-600 text-dark-700 w-140">View / Modify</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr key={client.id || client.clientId}>
                          <td><code>{client.clientId || '-'}</code></td>
                          <td>{client.name || '-'}</td>
                          <td>
                            <span className={`badge ${client.clientType === 'Export' ? 'bg-info' : 'bg-secondary'}`}>
                              {client.clientType || 'Domestic'}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleViewModify(client)}
                            >
                              <FaEdit className="me-1" /> Modify
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <nav className="mt-4" aria-label="Page navigation">
                  <ul className="pagination justify-content-center mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${page === currentPage ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(Math.min(pagination.last_page, currentPage + 1))}
                        disabled={currentPage === pagination.last_page}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>

                {/* Pagination Info */}
                <div className="text-center mt-3 text-muted">
                  <small>
                    Showing {((currentPage - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(currentPage * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} clients
                  </small>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientList;
